/**
 * Folder-upload transport — the batch loop that used to live inside
 * `useFileUpload`.
 *
 * It is deliberately React-free: an upload has to outlive the page that started
 * it (the user navigates to /chat the moment the project exists), so the engine
 * is owned by `UploadManagerContext` rather than by a component's state.
 *
 * The multi-request contract is load-bearing for the backend Cabinet ingest and
 * is reproduced here EXACTLY as it was:
 *   - files go up in batches of BATCH_SIZE, strictly sequentially;
 *   - `is_last_batch` is true only on the final batch of a job — it is what
 *     fires the ingest, so it must never fire over an incomplete project;
 *   - `overwrite_existing` is always true, which is what makes re-uploading a
 *     partial folder (the documented repair) idempotent rather than duplicating;
 *   - each file's sub-path travels as the multipart part filename, the only
 *     channel the backend reads it from.
 *
 * Two things a browser can do to a file that no server log will ever show
 * (Turtleback, 10-15: a 14 MB workbook "failed" twice and nothing reached the
 * backend): it can be unable to READ the file — open in Excel with AutoSave, or
 * an online-only OneDrive/SharePoint copy — or it can abort the request when
 * the file changes under it mid-send. So every file is read-checked before the
 * job starts, and a batch that fails at the network level is re-sent one file
 * at a time, so the one bad file fails alone and its batch-mates still land.
 * Either way the reason is reported per file, in words the user can act on.
 */

import { apiService } from "../services/api";
import { populateFormData } from "./fileUtils";
import { relativePathFor } from "./folderUpload";
import { getSessionToken } from "./sessionToken";

export interface UploadResponse {
	uploaded_files: Array<{
		original_filename: string;
		stored_filename: string;
		file_url: string;
		content_type: string;
		size: number;
		folder_path: string;
	}>;
	total_uploaded: number;
	errors: Array<{
		filename: string;
		error: string;
	}>;
	total_errors: number;
	project_id?: string;
	/**
	 * Present only when a batch failed and the loop STOPPED. Files uploaded
	 * before the failure are already persisted, so the fix is to retry the same
	 * folder with overwrite: `overwrite_existing` plus the content-addressed
	 * Cabinet cache make a re-upload idempotent — re-sending a file that already
	 * landed costs nothing and creates no duplicate.
	 */
	aborted?: {
		uploadedBeforeFailure: number;
		failed: string[];
		notAttempted: string[];
	};
}

export interface UploadJobRequest {
	files: File[];
	projectId: string;
	folderId: number;
	reportingPeriod: string;
	/** Called with 0-100 as each batch's bytes go out. */
	onProgress?: (progress: number) => void;
	/**
	 * Whether this job may flag its final batch (`is_last_batch`, which queues
	 * the deal's ingest). False when it is one part of a larger upload whose
	 * caller fires the ingest itself — the direct-upload engine's fallback.
	 */
	finalize?: boolean;
}

/**
 * Files per request. Sequential batches, never parallel within a job — the
 * `is_last_batch` contract depends on ordering. 4 per request (DOP-248): the
 * backend runs its per-file work off the event loop, so bigger batches just
 * cut request round-trips per folder.
 */
export const BATCH_SIZE = 4;

export const UNREADABLE_FILE_ERROR =
	"Your browser couldn't read this file. If it's open in Excel, or kept online-only in OneDrive or SharePoint, close it or make it available offline, then retry.";
export const FILE_NOT_SENT_ERROR =
	"Your browser couldn't send this file. If it's open in Excel, or syncing from OneDrive or SharePoint, close it or wait for the sync to finish, then retry.";

/** The request never completed: no response, so nothing reached the server. */
class UploadTransportError extends Error {}

/**
 * Whether the browser can read the file at all. Reading one byte is enough to
 * surface a locked or online-only file, and costs nothing on a healthy one.
 */
export const isReadable = (file: File): Promise<boolean> =>
	new Promise((resolve) => {
		// FileReader rather than `Blob.arrayBuffer()`: same answer, and it exists
		// in every browser the app supports (and in jsdom).
		const reader = new FileReader();
		reader.onload = () => resolve(true);
		reader.onerror = () => resolve(false);
		reader.onabort = () => resolve(false);
		try {
			reader.readAsArrayBuffer(file.slice(0, 1));
		} catch {
			resolve(false);
		}
	});

const splitIntoBatches = (files: File[]): File[][] => {
	const batches: File[][] = [];
	for (let i = 0; i < files.length; i += BATCH_SIZE) {
		batches.push(files.slice(i, i + BATCH_SIZE));
	}
	return batches;
};

/**
 * Send one batch. Resolves with the server's parsed body; rejects on any
 * transport-level failure so the caller can stop the loop.
 */
const uploadBatch = (
	batchFiles: File[],
	batchIndex: number,
	batchCount: number,
	isLastBatch: boolean,
	request: UploadJobRequest
): Promise<UploadResponse> => {
	const formData = new FormData();
	formData.append("is_last_batch", isLastBatch.toString());
	// Re-uploading a folder is the documented way to finish one that stopped
	// partway, so a file landing on a path it already occupies must UPDATE that
	// attachment rather than stack a duplicate beside it. Without this the retry
	// doubles every file that had succeeded.
	formData.append("overwrite_existing", "true");

	batchFiles.forEach((file) => {
		// The multipart part filename is the only channel the backend reads a
		// sub-path from — it lands in `FileAttachment.relative_path` via
		// `upload_files_to_storage`. For plain (non-folder) uploads this is just
		// `file.name`.
		formData.append("files", file, relativePathFor(file));
	});

	const populatedFormData = populateFormData(
		formData,
		request.folderId,
		request.projectId,
		request.reportingPeriod
	);

	return new Promise<UploadResponse>((resolve, reject) => {
		const xhr = new XMLHttpRequest();

		xhr.upload.addEventListener("progress", (e: ProgressEvent) => {
			if (!e.lengthComputable) return;
			const batchProgress = e.loaded / e.total;
			const previousBatchesProgress = (batchIndex / batchCount) * 100;
			const currentBatchProgress = (batchProgress / batchCount) * 100;
			request.onProgress?.(
				Math.round(previousBatchesProgress + currentBatchProgress)
			);
		});

		xhr.addEventListener("load", () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				try {
					resolve(JSON.parse(xhr.responseText));
				} catch {
					reject(new Error("Failed to parse response"));
				}
			} else {
				reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
			}
		});

		xhr.addEventListener("error", () => {
			reject(new UploadTransportError("Network error during upload"));
		});

		xhr.addEventListener("abort", () => {
			reject(new Error("Upload cancelled"));
		});

		const baseUrl = apiService.getBaseUrlForVersion("v3");
		xhr.open("POST", `${baseUrl}/files/upload`);

		const token = getSessionToken();
		if (token) {
			xhr.setRequestHeader("Authorization", `Bearer ${token}`);
		}

		xhr.send(populatedFormData);
	});
};

/**
 * Run one upload job to completion: every batch of one project's files, in
 * order, stopping at the first failure.
 *
 * Never rejects for a per-batch problem — a stopped loop is reported as an
 * `aborted` result, because the files that landed before it are real.
 */
export const runUploadJob = async (
	request: UploadJobRequest
): Promise<UploadResponse> => {
	const allUploadedFiles: UploadResponse["uploaded_files"] = [];
	const allErrors: UploadResponse["errors"] = [];
	let abortedAfterBatch: number | null = null;
	let notUploaded: string[] = [];

	// Read-check first. An unreadable file can't be sent; it is reported as
	// failed and the rest still go up. Because the project is then incomplete,
	// no batch may carry `is_last_batch` — the ingest fires on the retry that
	// completes it, exactly as after any other failure.
	const readability = await Promise.all(request.files.map(isReadable));
	const readable = request.files.filter((_, i) => readability[i]);
	const unreadable = request.files.filter((_, i) => !readability[i]);
	unreadable.forEach((file) =>
		allErrors.push({ filename: relativePathFor(file), error: UNREADABLE_FILE_ERROR })
	);
	const mayFinish = unreadable.length === 0 && request.finalize !== false;

	const batches = splitIntoBatches(readable);

	for (let i = 0; i < batches.length; i++) {
		const batch = batches[i];
		const isLast = mayFinish && i === batches.length - 1;
		try {
			const batchResponse = await uploadBatch(
				batch,
				i,
				batches.length,
				isLast,
				request
			);

			if (batchResponse.uploaded_files) {
				allUploadedFiles.push(...batchResponse.uploaded_files);
			}
			if (batchResponse.errors) {
				allErrors.push(...batchResponse.errors);
			}

			// A 200 with a non-empty `errors` array is a PARTIAL batch — some files
			// in it were rejected (unsupported type, storage failure). Continuing
			// would upload the rest of the folder around those gaps and then fire
			// the Cabinet ingest over an incomplete project, exactly the hole the
			// transport-level abort below exists to prevent. The batch's own
			// successes are real and kept; everything after it is not attempted.
			if (batchResponse.errors && batchResponse.errors.length > 0) {
				abortedAfterBatch = i;
				notUploaded = batches
					.slice(i + 1)
					.flat()
					.map(relativePathFor);
				console.error(
					`Batch ${i + 1} returned per-file errors — stopping:`,
					batchResponse.errors
				);
				break;
			}
		} catch (error: any) {
			// The request never got an answer — usually one file the browser
			// couldn't send. Re-send this batch one file at a time so that file
			// fails alone; if every file goes through, it was a blip and the job
			// carries on.
			if (error instanceof UploadTransportError && batch.length > 1) {
				let failedHere = false;
				for (let j = 0; j < batch.length; j++) {
					const file = batch[j];
					try {
						const single = await uploadBatch(
							[file],
							i,
							batches.length,
							isLast && j === batch.length - 1 && !failedHere,
							request
						);
						allUploadedFiles.push(...(single.uploaded_files ?? []));
						if (single.errors?.length) {
							allErrors.push(...single.errors);
							failedHere = true;
						}
					} catch (singleError: any) {
						allErrors.push({
							filename: relativePathFor(file),
							error:
								singleError instanceof UploadTransportError
									? FILE_NOT_SENT_ERROR
									: singleError?.message || "Upload failed",
						});
						failedHere = true;
					}
				}
				if (!failedHere) continue;
				abortedAfterBatch = i;
				notUploaded = batches
					.slice(i + 1)
					.flat()
					.map(relativePathFor);
				console.error(`Batch ${i + 1} had files that could not be sent — stopping`);
				break;
			}
			// STOP — do not continue the loop. Carrying on would upload later
			// batches while an earlier one is missing, producing a folder that looks
			// complete but has a hole in it, and would also let the final batch fire
			// the Cabinet ingest over a half-populated project.
			batch.forEach((file) => {
				allErrors.push({
					filename: relativePathFor(file),
					error:
						error instanceof UploadTransportError
							? FILE_NOT_SENT_ERROR
							: error?.message || "Upload failed",
				});
			});
			abortedAfterBatch = i;
			notUploaded = batches
				.slice(i + 1)
				.flat()
				.map(relativePathFor);
			console.error(`Batch ${i + 1} upload error — stopping:`, error);
			break;
		}
	}

	request.onProgress?.(100);

	return {
		uploaded_files: allUploadedFiles,
		total_uploaded: allUploadedFiles.length,
		errors: allErrors,
		total_errors: allErrors.length,
		project_id: request.projectId,
		aborted:
			abortedAfterBatch === null && unreadable.length === 0
				? undefined
				: {
						// Everything before the failing batch is already persisted — the
						// upload is PARTIAL, not undone.
						uploadedBeforeFailure: allUploadedFiles.length,
						failed: allErrors.map((e) => e.filename),
						notAttempted: notUploaded,
					},
	};
};
